import { faArrowCircleLeft, faArrowCircleRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { calculateElrGroupsForVideo } from "components/util/Geometry";
import { distanceInMetersBetweenChainPositions, getRoutePosAtTimestamp } from "components/util/GeometryUtilsV2";
import LoadingOverlay from "components/util/LoadingOverlay";
import { binarySearch, videoKeyToTimestamp } from "components/util/PlaylistUtils";
import { init as ecInit } from "echarts";
import { debounce, get, isNil } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { getOleStructures, routeSelected, websocketMessage } from "redux/actions";
import { routeGeomSelector, sourceOffsetSelector } from "redux/selectors";
import { boundedRange, boundedValue, useMouseMovement, useOnClick } from "../ChartV2/utils";
import { option } from "./chartOption";

const MIN_ZOOM = 30 * 1000; // 30 seconds
const MAX_ZOOM = 3 * 60 * 1000; // 3 minutes

const currentTimestampSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "timestamp"], 0);
const isPlayingSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "playerState"], "paused") === "playing";
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const sourceIndexSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "sourceIndex"], 0);
const playlistVideoSelector = (state) =>
    get(state.playlist.data.video, [get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "sourceIndex"], 0)], []);
const userConfigSelector = (state) => state.userDetails.userConfig;
const sessionIdSelector = (state) => state.playlist.data.routeID;
const sessionKeySelector = (state) => get(state.sessions, [state.playlist.data.routeID, "uuid"]);

function findPrevNext(position, data, extractor) {
    if (!data || data.length === 0) return { previous: null, next: null };

    const index = binarySearch(position, data, extractor);

    if (index === -1) return { previous: null, next: null };

    return {
        previous: data[index] ?? null, // An exact match === previous
        next: data[index + 1] ?? null,
    };
}

const Pantovision = () => {
    const dispatch = useDispatch();
    const currentTimestamp = useSelector(currentTimestampSelector);
    const isPlaying = useSelector(isPlayingSelector);
    const routeGeometry = useSelector(routeGeomSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const playlistVideo = useSelector(playlistVideoSelector);
    const userConfig = useSelector(userConfigSelector);
    const sourceOffset = useSelector(sourceOffsetSelector);
    const sourceIndex = useSelector(sourceIndexSelector);
    const sessionId = useSelector(sessionIdSelector);
    const sessionKey = useSelector(sessionKeySelector);

    const chartElementRef = useRef(null);
    const chartRef = useRef(null);
    const maxBoundsRef = useRef([null, null]);

    const [initialised, setInitialised] = useState(false);
    const [hasTraceData, setHasTraceData] = useState(false);
    const [data, setData] = useState(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState(0);
    const [oleStructures, setOleStructures] = useState({});

    const [zoomBounds, setZoomBounds] = useState([null, null]);

    useEffect(() => {
        if (sessionId && sessionKey) {
            dispatch(routeSelected(null));
            setInitialised(false);
            setHasTraceData(false);
            setData(null);
            setZoomBounds([null, null]);
            setSelectedTimestamp(0);
            maxBoundsRef.current = [null, null];

            const url = `https://panto.aivr.video/analysis/${sessionKey}/pantovision_analysis_results_${sessionId}_s${sourceIndex}.json`;

            fetch(url, {
                mode: "cors",
                headers: { "Content-Type": "application/json" },
            })
                .then((response) => {
                    if (!response.ok) {
                        return Promise.reject(response);
                    } else {
                        setHasTraceData(true);
                        return response.json();
                    }
                })
                .then((data) => {
                    setData(data);
                })
                .catch((error) => {
                    setHasTraceData(false);
                    console.log("DEBUG JB:", { error });
                })
                .finally(() => {
                    setInitialised(true);
                });
        }
    }, [dispatch, sessionId, sessionKey, sourceIndex]);

    useEffect(() => {
        if (!playlistVideo || playlistVideo.length === 0) return;

        const sessionMinTimestamp = videoKeyToTimestamp(playlistVideo[0][0]);
        const sessionMaxTimestamp = videoKeyToTimestamp(playlistVideo[playlistVideo.length - 1][0]);
        maxBoundsRef.current = [sessionMinTimestamp, sessionMaxTimestamp];

        let minZoomTimestamp = sessionMinTimestamp;
        let maxZoomTimestamp = minZoomTimestamp + MAX_ZOOM;

        if (currentTimestamp > 0) {
            const halfSpan = MAX_ZOOM / 2;
            minZoomTimestamp = currentTimestamp - halfSpan;
            maxZoomTimestamp = currentTimestamp + halfSpan;
        }

        const newBounds = boundedRange(sessionMinTimestamp, minZoomTimestamp, maxZoomTimestamp, sessionMaxTimestamp);

        setZoomBounds(newBounds);
        viewBounds(newBounds);
        setSelectedTimestamp(currentTimestamp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlistVideo]);

    useEffect(() => {
        if (sessionId && currentTimestamp > 0) {
            dispatch(routeSelected(sessionId, 0));
        }
    }, [dispatch, sessionId, currentTimestamp]);

    const routeIndexes = useMemo(() => {
        const routePositionData = get(routeGeometry, ["distance_to_route_position"], []);

        return routePositionData.length > 0 ? calculateElrGroupsForVideo(playlistVideo, routeGeometry, routeSystemID) : null;
    }, [playlistVideo, routeGeometry, routeSystemID]);

    useEffect(() => {
        if (!chartRef.current || !routeIndexes) return;

        chartRef.current.setOption(
            {
                dataset: [
                    {
                        id: "overview_dataset",
                        source: routeIndexes,
                    },
                ],
            },
            false,
        );
    }, [routeIndexes]);

    useEffect(() => {
        if (!routeIndexes || routeIndexes?.length === 0) {
            return;
        }

        dispatch(getOleStructures(routeIndexes))
            .then(({ ole_structures }) => {
                setOleStructures(ole_structures);
            })
            .catch((error) => {
                console.error("Failed to fetch OLE structures:", error);
            });
    }, [dispatch, routeIndexes]);

    useEffect(() => {
        if (!chartElementRef.current) return;

        const chart = ecInit(chartElementRef.current, "dark", {
            renderer: "canvas",
            animation: false,
        });

        chart.setOption(option);
        chartRef.current = chart;

        return () => {
            chart.dispose();
            chartRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (currentTimestamp) {
            setSelectedTimestamp(currentTimestamp);
        }
    }, [currentTimestamp]);

    const viewBounds = useCallback((bounds) => {
        if (!chartRef.current) return;

        const boundedHighlight = boundedRange(maxBoundsRef.current[0], bounds[0], bounds[1], maxBoundsRef.current[1]);
        chartRef.current.setOption({
            xAxis: (chartRef.current.getOption().xAxis ?? [])
                .filter((axis) => axis.id !== "overview_x")
                .map((axis) => ({
                    id: axis.id,
                    min: boundedHighlight[0],
                    max: boundedHighlight[1],
                })),
            series: [
                {
                    id: "current_view_series",
                    data: [boundedHighlight],
                },
            ],
        });
    }, []);

    const updateCurrentPosition = useCallback(
        (timestamp, zoomBounds, isPlaying = false) => {
            if (!chartRef.current) return;

            const currentOption = chartRef.current.getOption();
            if (!currentOption) return;

            if (isPlaying) {
                const [min, max] = zoomBounds;
                const [dataMin, dataMax] = maxBoundsRef.current;

                const isVisible = timestamp >= min && timestamp <= max;
                if (isPlaying && !isVisible && timestamp >= dataMin && timestamp <= dataMax) {
                    const range = max - min;
                    let newStart = timestamp - range / 2;
                    let newEnd = timestamp + range / 2;

                    if (newStart < dataMin) {
                        newStart = dataMin;
                        newEnd = dataMin + range;
                    } else if (newEnd > dataMax) {
                        newEnd = dataMax;
                        newStart = dataMax - range;
                    }

                    setZoomBounds([newStart, newEnd]);
                    viewBounds([newStart, newEnd]);
                }
            }

            chartRef.current.setOption({
                series: [
                    {
                        id: "selected_timestamp_position_series",
                        data: [[timestamp]],
                    },
                    {
                        id: "selected_timestamp_overview_series",
                        data: [[timestamp]],
                    },
                ],
            });
        },
        [viewBounds],
    );

    useEffect(() => {
        updateCurrentPosition(selectedTimestamp, zoomBounds, isPlaying);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimestamp]);

    const onResize = debounce(({ bounds }) => {
        if (chartRef.current) {
            chartRef.current.resize();
        }
    }, 500);

    useEffect(() => {
        if (data) {
            const minTimestamp = data.analysis_results[0].ts;
            const maxTimestamp = data.analysis_results[data.analysis_results.length - 1].ts;
            const maxZoomIndex = binarySearch(minTimestamp + MAX_ZOOM, data.analysis_results, (geom) => geom.ts);
            const maxZoomTimestamp = data.analysis_results[maxZoomIndex].ts;

            setZoomBounds([minTimestamp, maxZoomTimestamp]);
            maxBoundsRef.current = [minTimestamp, maxTimestamp];

            if (!chartRef.current) return;

            chartRef.current.setOption({
                yAxis: [
                    {
                        id: "height_y",
                        min: data.train_height_mm,
                    },
                ],
                series: [
                    {
                        id: "height_mark_area_series",
                        markArea: {
                            data: [
                                [
                                    { yAxis: 6500, itemStyle: { color: "red" } },
                                    { yAxis: data.pan_max_elevation_mm, itemStyle: { color: "red" } },
                                ],
                                [
                                    { yAxis: data.pan_max_elevation_mm, itemStyle: { color: "green", opacity: 0.15 } },
                                    { yAxis: data.pan_min_elevation_mm, itemStyle: { color: "green", opacity: 0.15 } },
                                ],
                                [
                                    { yAxis: data.pan_min_elevation_mm, itemStyle: { color: "red" } },
                                    { yAxis: data.train_height_mm, itemStyle: { color: "red" } },
                                ],
                            ],
                        },
                    },
                    {
                        id: "stagger_mark_area_series",
                        markArea: {
                            data: [
                                [
                                    { yAxis: -550, itemStyle: { color: "red" } },
                                    { yAxis: -data.red_zone_mm, itemStyle: { color: "red" } },
                                ],
                                [
                                    { yAxis: -data.red_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                    { yAxis: -data.green_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                ],
                                [
                                    { yAxis: data.green_zone_mm * -1, itemStyle: { color: "green" } },
                                    { yAxis: data.green_zone_mm, itemStyle: { color: "green" } },
                                ],
                                [
                                    { yAxis: data.green_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                    { yAxis: data.red_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                ],
                                [
                                    { yAxis: data.red_zone_mm, itemStyle: { color: "red" } },
                                    { yAxis: 550, itemStyle: { color: "red" } },
                                ],
                            ],
                        },
                    },
                ],
            });
        }
    }, [data]);

    useEffect(() => {
        if (!data) return;

        const result = {
            height: [],
            smoothed: [],
            acceleration: [],
            contact: [],
            unclassified: [],
            catenary: [],
            poleEventsBySide: [],
            poleEvents: [],
        };

        const range = (zoomBounds[1] - zoomBounds[0]) / 2;
        const startIndex = binarySearch(zoomBounds[0] - range, data.analysis_results, (geom) => geom.ts);
        const endIndex = binarySearch(zoomBounds[1] + range, data.analysis_results, (geom) => geom.ts);

        for (let i = startIndex; i <= endIndex; i++) {
            const d = data.analysis_results[i];

            if (d.pan_elevation_mm !== null) {
                result.height.push([d.ts, d.pan_elevation_mm]);
            }

            result.smoothed.push([d.ts, d.pan_elevation_mm_smoothed]);
            result.acceleration.push([d.ts, d.acceleration]);

            if (d.pole_events !== undefined) {
                result.poleEvents.push([d.ts, -12000], [d.ts, 12000]);

                d.pole_events.forEach((pole) => {
                    result.poleEventsBySide.push([d.ts, pole === "left" ? -550 : 550]);
                });
            }

            d.wires.forEach((w) => {
                if (result[w.class]) {
                    result[w.class].push([d.ts, w.stagger_mm]);
                }
            });
        }

        chartRef.current.setOption({
            series: [
                {
                    id: "height_pole_event_series",
                    data: result.poleEvents,
                },
                {
                    id: "height_series",
                    data: result.height,
                },
                {
                    id: "height_smooth_series",
                    data: result.smoothed,
                },
                {
                    id: "stagger_pole_event_series",
                    data: result.poleEventsBySide,
                },
                {
                    id: "stagger_contact_wire_series",
                    data: result.contact,
                },
                {
                    id: "stagger_catenary_wire_series",
                    data: result.catenary,
                },
                {
                    id: "stagger_unclassified_wire_series",
                    data: result.unclassified,
                },

                {
                    id: "acceleration_pole_event_series",
                    data: result.poleEvents,
                },
                {
                    id: "acceleration_series",
                    data: result.acceleration,
                },
            ],
        });
    }, [data, zoomBounds]);

    const nextIcon = useMemo(() => ReactDOMServer.renderToString(<FontAwesomeIcon icon={faArrowCircleRight} />), []);
    const prevIcon = useMemo(() => ReactDOMServer.renderToString(<FontAwesomeIcon icon={faArrowCircleLeft} />), []);

    const tooltipFormatter = useCallback(
        (params) => {
            if (params.length > 0) {
                const position = {};

                if (params[0].axisValue) {
                    const calculatedRoutePosition = getRoutePosAtTimestamp(params[0].axisValue, routeGeometry, routeSystemID);
                    if (calculatedRoutePosition) {
                        position.positionString = calculatedRoutePosition.to_string(userConfig.elr_units, sourceOffset);

                        if (calculatedRoutePosition.system === "ELR Mile & Chain") {
                            const key = `${calculatedRoutePosition.elr}_${calculatedRoutePosition.track}`;
                            const structures = oleStructures[key];
                            const { previous, next } = findPrevNext(calculatedRoutePosition.position, structures, (s) => s.position);

                            if (previous?.id) {
                                position.previousStructure = {
                                    id: previous.id,
                                    distance: distanceInMetersBetweenChainPositions(previous.position, calculatedRoutePosition.position),
                                };
                            }

                            if (next?.id) {
                                position.nextStructure = {
                                    id: next.id,
                                    distance: distanceInMetersBetweenChainPositions(next.position, calculatedRoutePosition.position),
                                };
                            }
                        }
                    }
                }

                return `<div class="Tooltip">
                    ${
                        !isNil(position)
                            ? `<header>
                                ${position.positionString ? `<div><span><strong>${position.positionString}</strong></span></div>` : ""}
                                ${
                                    position.nextStructure || position.previousStructure
                                        ? `<label>Nearby Structures</label>
                                    <div class="nearby-structures">
                                        ${position.previousStructure ? `<div class="structure left"><div><span><strong>${position.previousStructure.id}</strong></span></div><div>${prevIcon} ${position.previousStructure.distance}m</div></div>` : ""}
                                        ${position.nextStructure ? `<div class="structure right"><div><span><strong>${position.nextStructure.id}</strong></span></div><div>${[position.nextStructure.distance]}m ${nextIcon}</div></div>` : ""}
                                    </div>`
                                        : ""
                                }
                            </header>`
                            : ""
                    }
                    ${params
                        .map((p) => {
                            return `<div>
                                <div>
                                    ${p.marker}
                                    <span>${p.seriesName}</span>
                                </div>
                                <span><strong>${Math.round(p.data[1])}</strong>${p.axisId === "acceleration_x" ? "mm/s²" : "mm"}</span>
                            </div>`;
                        })
                        .join("")}
                </div>`;
            }
        },
        [nextIcon, oleStructures, prevIcon, routeGeometry, routeSystemID, sourceOffset, userConfig.elr_units],
    );

    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.setOption({
            tooltip: {
                formatter: tooltipFormatter,
            },
        });
    }, [tooltipFormatter]);

    const onSelectTimestamp = useCallback(
        (timestamp) => {
            setSelectedTimestamp(timestamp);
            dispatch(websocketMessage({ action: "request_position", timestamp: timestamp }));
        },
        [dispatch],
    );

    const onPositionGridClick = useCallback(
        (item) => {
            const timestamp = item.position_grid[0];
            const index = binarySearch(timestamp, data.analysis_results, (geom) => geom.ts);
            onSelectTimestamp(data.analysis_results[index].ts);
        },
        [data, onSelectTimestamp],
    );

    useOnClick(chartRef.current, ["position_grid"], onPositionGridClick);

    const selectBounds = useCallback(
        (bounds) => {
            setZoomBounds(bounds);
            viewBounds(bounds);
        },
        [viewBounds],
    );

    const goToTimestamp = useCallback(
        (timestamp) => {
            onSelectTimestamp(timestamp);
            const halfSpan = (zoomBounds[1] - zoomBounds[0]) / 2;
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - halfSpan, timestamp + halfSpan, maxBoundsRef.current[1]);
            selectBounds(newBounds);
        },
        [onSelectTimestamp, selectBounds, zoomBounds],
    );

    const dragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            const timestamp = coordinates[0];
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - MAX_ZOOM / 2, timestamp + MAX_ZOOM / 2, maxBoundsRef.current[1]);
            viewBounds(newBounds);
        },
        [viewBounds],
    );

    const endDragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            goToTimestamp(coordinates[0]);
        },
        [goToTimestamp],
    );

    useMouseMovement(chartRef.current, "overview_grid", dragCallback, endDragCallback);

    const calculateNewBounds = (dragState, zoomBounds, maxBounds) => {
        const [currentMin, currentMax] = zoomBounds;
        const [minBound, maxBound] = maxBounds;

        if (dragState.delta[2]) {
            // Zoom
            const timestamp = dragState.start[0];
            const range = currentMax - currentMin;

            let newRange = range * Math.pow(4, dragState.delta[2]);

            // Clamp range
            newRange = boundedValue(MIN_ZOOM, newRange, MAX_ZOOM);

            const ratio = (timestamp - currentMin) / range;

            let newStart = timestamp - ratio * newRange;
            let newEnd = newStart + newRange;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + newRange;
            }
            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - newRange;
            }

            return [newStart, newEnd];
        } else {
            // Pan
            const timestampDelta = Math.round(dragState.delta[0]);

            let newStart = currentMin + timestampDelta;
            let newEnd = currentMax + timestampDelta;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + (currentMax - currentMin);
            }

            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - (currentMax - currentMin);
            }

            return [newStart, newEnd];
        }
    };

    const boundedZoomRange = (min, low, high, max) => {
        let newLow = Math.max(min, low);
        let newHigh = Math.min(max, high);

        let range = newHigh - newLow;

        if (range < MIN_ZOOM) {
            // Expand to minimum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MIN_ZOOM / 2);
            newHigh = Math.min(max, center + MIN_ZOOM / 2);
        } else if (range > MAX_ZOOM) {
            // Reduce to maximum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MAX_ZOOM / 2);
            newHigh = Math.min(max, center + MAX_ZOOM / 2);
        }

        return [newLow, newHigh];
    };

    const positionDragCallback = useCallback(
        (dragState) => {
            if (chartRef.current) {
                const newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);

                viewBounds(newBounds);
            }
        },
        [viewBounds, zoomBounds],
    );

    const positionEndDragCallback = useCallback(
        (dragState) => {
            if (chartRef.current) {
                let newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);
                newBounds = boundedZoomRange(maxBoundsRef.current[0], newBounds[0], newBounds[1], maxBoundsRef.current[1]);

                selectBounds(newBounds);
            }
        },
        [selectBounds, zoomBounds],
    );

    useMouseMovement(chartRef.current, "position_grid", positionDragCallback, positionEndDragCallback);

    return (
        <div className="PantovisionContainer">
            <Measure
                bounds
                onResize={onResize}>
                {({ measureRef }) => (
                    <div
                        className="PantovisionInner"
                        ref={measureRef}>
                        <div
                            className={`Pantovision${!initialised || !hasTraceData ? " hidden" : ""}`}
                            ref={chartElementRef}></div>
                        {!initialised && (
                            <div className="overlay">
                                <LoadingOverlay loading={true} />
                            </div>
                        )}
                        {initialised && !hasTraceData && (
                            <div className="overlay widget__Info center">
                                <p>No data for this session.</p>
                            </div>
                        )}
                    </div>
                )}
            </Measure>
        </div>
    );
};

export default Pantovision;
